import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import { Module } from 'app/library/module/models/module.model';
import { ModuleState } from 'app/library/module/models/module.state';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import {
    CreateModuleSuccess,
    DeleteModule,
    DeselectSelectedModules,
    FetchTargetModules,
    FetchTargetModulesFailure,
    SearchModules,
    SelectFilteredModules,
    SetModules,
    ToggleSelectedModule,
} from '../actions';
import { AddModules } from '../actions/add-entities.action';
import { FetchTargetModulesSuccess } from '../actions/index';
import { ModuleApiService } from '../services/module.api-service';
import { ModulesStateModel } from './modules.state-model';

@State<ModulesStateModel>({
    name: 'modules',
    defaults: new ModulesStateModel(),
})
@Injectable()
export class ModulesState extends SubentityState<Module> {
    @Selector([ModuleState.getEntities])
    static getFilteredEntities(state: ModulesStateModel, modules: Module[]): Module[] {
        if (!modules) {
            return null;
        }

        return modules
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, Module], b: [number, Module]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, Module]) => x[1]);
    }

    @Selector()
    static getSelectedEntityIds(state: ModulesStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getSearchTerm(state: ModulesStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTargetModules(state: ModulesStateModel): Module[] {
        return state.targetModulesList;
    }

    constructor(private store: Store, private action$: Actions, private _moduleService: ModuleApiService) {
        super(null, 0);

        this._propstoSearch = [...Module.props_to_search];
    }

    @Action(AddModules)
    addEntities(ctx: StateContext<ModulesStateModel>, action: AddModules): void {
        const modules = this.store.selectSnapshot(ModuleState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: action.payload.map((entity) => entity.id) } }, modules);
    }

    @Action(CreateModuleSuccess)
    createEntity(ctx: StateContext<ModulesStateModel>, action: CreateModuleSuccess): void {
        const modules = this.store.selectSnapshot(ModuleState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: [action.payload.id] } }, modules);
    }

    @Action(DeleteModule)
    deleteEntity({ getState, patchState }: StateContext<ModulesStateModel>, { payload }: DeleteModule): void {
        const state = getState();

        patchState({
            list: [...state.list.filter((subentityId) => subentityId !== payload)],
            selected: [...state.selected.filter((subentityId) => subentityId !== payload)],
            filtered: [...state.filtered.filter((subentityId) => subentityId !== payload)],
        });
    }

    @Action(SetModules)
    setEntities(ctx: StateContext<ModulesStateModel>, action: SetModules): void {
        this._setSubentities(ctx, action, null);
    }

    @Action(ToggleSelectedModule)
    toggleSelect(ctx: StateContext<ModulesStateModel>, action: ToggleSelectedModule): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredModules)
    selectAll(ctx: StateContext<ModulesStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedModules)
    deselectAll(ctx: StateContext<ModulesStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchModules)
    search(ctx: StateContext<ModulesStateModel>, action: SearchModules): void {
        const modules = this.store.selectSnapshot(ModuleState.getEntities);

        this._searchSubentities(ctx, action, modules);
    }

    @Action(FetchTargetModules)
    fetchTargetModules(ctx: StateContext<ModulesStateModel>, action: FetchTargetModules): void {
        this._moduleService.getTargetModules(action.payload).subscribe(
            (entities) => {
                ctx.patchState({
                    targetModulesList: entities,
                });

                ctx.dispatch(new FetchTargetModulesSuccess(entities));
            },
            (error: HttpErrorResponse) => {
                ctx.dispatch(new FetchTargetModulesFailure());
            }
        );
    }
}
